import { FpAxios } from '@/services/FpAxios';
import { DivisionFilter, FiltersResponse } from '@/models/Filter';
import Division, { DivisionKind, FinCRM_Division } from '@/models/Division';
import AuthService from '@/modules/user/AuthService';
import { DivisionMember, DivisionMemberDTO } from '@/models/DivisionMember';
import processProxy from '@/env/processProxy';
import multiformDictionaries from '@/modules/multiForm/shared/MultiformDictionaries';
import { HistoryElement, HistoryElementDTO } from '@/models/HistoryElement';
import { AccreditationInvitation } from '@/modules/accredition/models/backend/AccreditationInvitation';

export interface DivisionCompanyData {
  divisionId: number;
  divisionName: string;
  ownerName?: string;
  ownerId?: number;
  companyName?: string;
  companyAddress?: string;
  companyAddressLines?: string[];
  nip?: string;
  regon?: string;
  phone?: string;
}

export interface ActivateDivisionResponse {
  success: boolean;
  conflictingDivision?: DivisionCompanyData;
}

export interface DeactivateDivisionResponse {
  success: boolean;
  hasActiveDeals: boolean;
  leader?: {
    id: number;
    name: string;
    email: string;
    phone?: string;
  };
}

class DivisionApi {
  private divisionAxios = FpAxios.create();
  readonly baseUrl = 'divisions';

  async filterDivisions(filters?: DivisionFilter): Promise<FiltersResponse<Division>> {
    if (AuthService.User!.isVerifier || !AuthService.User!.isUserOf.FINPACK) {
      filters!.organisationId = AuthService.User!.organisationId || null;
    }
    const response = await this.divisionAxios.post<FiltersResponse<Division>>(`${this.baseUrl}/search`, filters ? filters.apiData : {});
    return response.data;
  }

  async multiSearch(filters?: DivisionFilter): Promise<FiltersResponse<DivisionMember>> {
    const response = await this.divisionAxios.post<FiltersResponse<DivisionMember>>(`${this.baseUrl}/multi-search`, filters ? filters.apiData : {});
    return response.data;
  }

  async getMembersOfFilteredDivision(filters?: DivisionFilter): Promise<FiltersResponse<DivisionMember>> {
    if (AuthService.User!.isVerifier || !AuthService.User!.isUserOf.FINPACK) {
      filters!.organisationId = AuthService.User!.organisationId || null;
    }
    const response = await this.divisionAxios.post<FiltersResponse<DivisionMember>>(`${this.baseUrl}/search`, filters ? filters.apiData : {});
    response.data.items = response.data.items.map(
      (divisionMemberDto: DivisionMemberDTO) => new DivisionMember(divisionMemberDto));
    return response.data;
  }

  async fetchDivisionsChiefs(onlyMainChiefs: boolean = false) {
    const response = await this.divisionAxios.get(`${this.baseUrl}/chiefs?isMainChief=${onlyMainChiefs}`);
    return response.data;
  }

  async getDivisionKinds(): Promise<Array<DivisionKind>> {
    const response = await this.divisionAxios.get(`${processProxy.VUE_APP_OLD_BASE_URL}/organisations/api/divisiontypes`);
    return response.data;
  }

  async addDivision(division: FinCRM_Division): Promise<FinCRM_Division> {
    const response = await this.divisionAxios.post(`${processProxy.VUE_APP_OLD_BASE_URL}/organisations/api/organisations/${division.organisation?.id}/divisions`,
      division);
    return response.data;
  }

  async editDivision(division: FinCRM_Division): Promise<void> {
    await this.divisionAxios.patch(`${processProxy.VUE_APP_OLD_BASE_URL}/organisations/api/organisations/${division.organisationId}/divisions/${division.id}/fields`, division);
  }

  async getDivisionInfo(id: number): Promise<Division> {
    const response = await this.divisionAxios.get<Division>(`${this.baseUrl}/${id}`);
    return new Division(response.data);
  }

  async getDivisionsForUser(userId: number): Promise<Array<Division>> {
    const response = await this.divisionAxios.get<Array<Division>>(`${this.baseUrl}/user/${userId}`);
    return response.data;
  }

  async getDivisionsForDivisionMember(divisionMemberId: number): Promise<Array<{ divisionId: number, divisionName: string }>> {
    const response = await this.divisionAxios.post<Array<{ divisionId: number, divisionName: string }>>(`${this.baseUrl}/member-divisions`, { userId: divisionMemberId, });
    return response.data;
  }

  async deleteDivision(organisationId: number, id: number): Promise<void> {
    await this.divisionAxios.delete(`${processProxy.VUE_APP_OLD_BASE_URL}/organisations/api/organisations/${organisationId}/divisions/${id}`);
  }

  async getDivisionsForLendiCare(): Promise<FinCRM_Division[]> {
    const response = await this.divisionAxios.get(`${processProxy.VUE_APP_OLD_BASE_URL}/organisations/api/organisations/lendi-care/networks`);
    return response.data;
  }

  async getDivisionHistory(divisionId: number, page: number = 1): Promise<Array<HistoryElement<any>>> {
    const response = await this.divisionAxios.get(`${this.baseUrl}/${divisionId}/history`, { params: { page, }, });
    const data: any = response.data;
    const items: Array<HistoryElementDTO<any>> = Array.isArray(data) ? data : (data?.items ?? []);
    return items.map((historyElementDTO: HistoryElementDTO<any>) => new HistoryElement(historyElementDTO));
  }

  async getDivisionHistoryByPage(
    divisionId: number,
    filter?: { page?: number; rowsPerPage?: number }
  ): Promise<FiltersResponse<HistoryElement<any>>> {
    const page = filter?.page ?? 1;
    const rowsPerPage = filter?.rowsPerPage ?? 10;
    const response = await this.divisionAxios.get(`${this.baseUrl}/${divisionId}/history`, {
      params: { page, pageSize: rowsPerPage, },
    });
    const data: any = response.data;
    const items: Array<HistoryElementDTO<any>> = Array.isArray(data) ? data : (data?.items ?? []);
    return {
      items: items.map((dto: HistoryElementDTO<any>) => new HistoryElement(dto)),
      quantity: data.quantity,
      page: Array.isArray(data) ? page : (data?.page ?? page),
    };
  }

  async getActiveDivisionByNip(nip: string): Promise<DivisionCompanyData | null> {
    try {
      const response = await this.divisionAxios.get<any>(`${this.baseUrl}/active-by-nip/${nip}`);
      return this.mapCompanyToDivisionCompanyData(response.data);
    } catch (e) {
      if (e.response?.status === 404 || e.response?.status === 400) {
        return null;
      }
      throw e;
    }
  }

  async activateDivision(_organisationId: number, divisionId: number): Promise<void> {
    await this.divisionAxios.put(`${this.baseUrl}/${divisionId}/activate`);
  }

  async deactivateDivision(_organisationId: number, divisionId: number): Promise<void> {
    await this.divisionAxios.put(`${this.baseUrl}/${divisionId}/deactivate`);
  }
  
  async verifyDivisionActivation(divisionId: number): Promise<{ canActivate: boolean; conflictingDivision?: DivisionCompanyData }> {
    const response = await this.divisionAxios.get<any[]>(`${this.baseUrl}/${divisionId}/activate/verify`);
    const list: any[] = response.data ?? [];
    if (list.length > 0) {
      return {
        canActivate: false,
        conflictingDivision: this.mapCompanyToDivisionCompanyData(list[0]),
      };
    }
    return { canActivate: true };
  }

  /**
   * Pobiera zarchiwizowane zaproszenia akredytacyjne dywizji.
   * Zaproszenia archiwizowane są m.in. przy aktywacji dywizji z konfliktem NIP,
   * dzięki czemu ich dane (ekspert, status) pozostają widoczne na dywizji.
   */
  async getArchivedInvitations(divisionId: number): Promise<AccreditationInvitation[]> {
    const response = await this.divisionAxios.get<AccreditationInvitation[]>(
      `${this.baseUrl}/${divisionId}/archived-invitations`
    );
    return response.data.map((dto) => new AccreditationInvitation(dto));
  }

  /**
   * Pobiera snapshot zarchiwizowanej dywizji (OrganisationDivision).
   */
  async getDivisionArchive(divisionId: number): Promise<OrganisationDivisionArchive> {
    const response = await this.divisionAxios.get<OrganisationDivisionArchive>(
      `${this.baseUrl}/${divisionId}/archive`
    );
    return response.data;
  }

  private mapCompanyToDivisionCompanyData(company: any): DivisionCompanyData {
    const address = company.address;
    const countryName = address?.country
      ? multiformDictionaries.countriesDictionary.find(({ type, }) => type === address.country)?.name_pl ?? address.country
      : undefined;
    const streetLine = [address?.street, address?.streetNum].filter(Boolean).join(' ');
    const companyAddress = address
      ? [streetLine, address.zip, address.city, countryName].filter(Boolean).join(', ')
      : undefined;
    const companyAddressLines = address
      ? [
        streetLine,
        [address.zip, address.city].filter(Boolean).join(', '),
        countryName,
      ].filter(Boolean)
      : undefined;
    return {
      divisionId: company.divisionId,
      divisionName: company.divisionName ?? company.name ?? '-',
      ownerName: company.ownerName,
      ownerId: company.ownerId,
      companyName: company.name ?? company.companyName,
      companyAddress,
      companyAddressLines,
      nip: company.nip,
      regon: company.regon,
      phone: company.phone,
    };
  }
}

/** Snapshot zarchiwizowanej dywizji (OrganisationDivision) */
export interface OrganisationDivisionArchive {
  id: number;
  name: string;
  organisationId?: number;
  companyName?: string;
  nip?: string;
  regon?: string;
  [key: string]: any;
}

export default new DivisionApi();
